import React, { useState, useEffect, useRef ,useMemo} from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  useTheme,
  styled,
  alpha,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  useMediaQuery,
} from '@mui/material'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import ArticleCard from './ArticleCard'
import axios from 'axios'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import apiService from '../../services/ApiService'
import { Enhanced404Handler } from '../Error404'
// Define styled components with animation
const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
}))

const AnimatedHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  opacity: 0,
  animation: 'fadeInDown 0.5s ease 0.2s forwards',
  '@keyframes fadeInDown': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  opacity: 0,
  animation: 'fadeIn 0.5s ease 0.4s forwards',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
}))

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  height: 48,
  borderRadius: theme.spacing(1),
  marginRight: theme.spacing(2),
  color: '#ffcf51', // Gold color
  backgroundColor: alpha('#ffcf51', 0.1),
}))

const HierarchicalArticlesView = () => {
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { country, lawShortName, path } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [articles, setArticles] = useState([])
  const [sectionInfo, setSectionInfo] = useState({
    title: '',
    pathSegments: [],
  })

  const articlesRef = useRef(null)

  // Theme colors for consistent styling
  const themeColors = {
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
    background: '#f8f8f8',
    border: '#e0e0e0',
  }

  // Validate input parameters

  const validateInputs = () => {
    if (!country || !lawShortName || !path) {
      return {
        isValid: false,

        error: 'Missing required parameters: country, law name, or path',
      }
    }

    // Check for empty or whitespace-only strings

    if (
      country.trim() === '' ||
      lawShortName.trim() === '' ||
      path.trim() === ''
    ) {
      return {
        isValid: false,

        error: 'Parameters cannot be empty',
      }
    }

    // Validate path format (should contain letters, numbers, and hyphens)

    const pathRegex = /^[a-zA-Z]+\d+(-[a-zA-Z]+\d+)*$/

    if (!pathRegex.test(path)) {
      return {
        isValid: false,

        error:
          'Invalid path format. Expected format: "part1", "part2-section1", or "part2-section1-chapter3"',
      }
    }

    // Check for consecutive hyphens or hyphens at start/end

    if (path.includes('--') || path.startsWith('-') || path.endsWith('-')) {
      return {
        isValid: false,

        error:
          'Invalid path format. Avoid consecutive hyphens or hyphens at start/end',
      }
    }

    return { isValid: true, error: null }
  }

  // Parse path into segments for breadcrumbs and display

  const parsePathSegments = (pathString) => {
    if (!pathString) return []

    const segments = pathString.split('-')

    const pathSegments = []

    let currentPath = ''

    segments.forEach((segment, index) => {
      if (index > 0) currentPath += '-'

      currentPath += segment

      // Extract type and number from segment (e.g., "part2" -> type: "part", number: "2")

      const match = segment.match(/^([a-zA-Z]+)(\d+)$/)

      if (match) {
        const [, type, number] = match

        pathSegments.push({
          type: type.toLowerCase(),

          number,

          displayName: `${
            type.charAt(0).toUpperCase() + type.slice(1)
          } ${number}`,

          fullPath: currentPath,
        })
      } else {
        // Handle cases where segment doesn't match the expected pattern

        pathSegments.push({
          type: segment.toLowerCase(),

          number: '',

          displayName: segment.charAt(0).toUpperCase() + segment.slice(1),

          fullPath: currentPath,
        })
      }
    })

    return pathSegments
  }

  // Generate section title from path segments

  const generateSectionTitle = (pathSegments) => {
    if (!pathSegments || pathSegments.length === 0) return 'Articles'

    const lastSegment = pathSegments[pathSegments.length - 1]

    return lastSegment.displayName
  }
  // Generate breadcrumb items from path segments

  const generateBreadcrumbs = (pathSegments) => {
    const breadcrumbs = [
      {
        label: 'Home',

        href: '/',

        isActive: false,
      },

      {
        label: lawShortName.replaceAll('-', ' ').toUpperCase(),

        href: `/laws/${country}/${lawShortName}`,

        isActive: false,
      },
    ]

    // Add intermediate path segments as breadcrumbs

    pathSegments.forEach((segment, index) => {
      const isLast = index === pathSegments.length - 1

      breadcrumbs.push({
        label: segment.displayName,

        href: isLast
          ? null
          : `/laws/${country}/${lawShortName}/${segment.fullPath}`,

        isActive: isLast,
      })
    })

    return breadcrumbs
  }
  // Fetch articles data

  useEffect(() => {
    const fetchArticles = async () => {
      // Validate inputs first

      const validation = validateInputs()

      if (!validation.isValid) {
        setError(validation.error)

        setLoading(false)

        return
      }
      try {
        setLoading(true)
        setError(null)

        // Use the API endpoint to fetch articles under a specific first-level path (RESTRICTIVE API)
        // const response = await apiService.get(
        //   `/api/v1/laws/${country}/${lawShortName}/first-level?path=${structureType}/${structureNumber}`
        // )

        // to fetch articles at any level for e.g. part2-section1-chapter3
        const response = await apiService.get(
          `/api/v1/articles/${country}/${lawShortName}/${path}`
        )

        // Set the articles data
        setArticles(response)
        // Parse path and set section info

        const pathSegments = parsePathSegments(path)

        setSectionInfo({
          title: generateSectionTitle(pathSegments),

          pathSegments,
        })
      } catch (err) {
        console.error('Error fetching articles:', err)
        let errorMessage = 'Failed to load articles. Please try again.'

        if (err.response?.status === 404) {
          errorMessage = `No articles found for the specified path "${path}" in "${lawShortName
            .replaceAll('-', ' ')
            .toUpperCase()}" law.`
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message
        }

        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    if (country && lawShortName && path) {
      fetchArticles()
    }
  }, [country, lawShortName, path])

  // Animation for articles - staggered appearance
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px',
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          // Add staggered animation delay based on index
          setTimeout(() => {
            entry.target.classList.add('animate')
          }, index * 100)
        }
      })
    }, observerOptions)

    const articleElements =
      articlesRef.current?.querySelectorAll('.article-item')
    if (articleElements) {
      articleElements.forEach((item) => {
        observer.observe(item)
      })
    }

    return () => {
      if (articleElements) {
        articleElements.forEach((item) => {
          observer.unobserve(item)
        })
      }
    }
  }, [articles])
  // Generate breadcrumbs
const breadcrumbItems = useMemo(
  () =>
    sectionInfo.pathSegments
      ? generateBreadcrumbs(sectionInfo.pathSegments)
      : [],
  [sectionInfo.pathSegments, country, lawShortName]
)
  return (
    <Box
      sx={{
        minHeight: '100vh',
        paddingBottom: { xs: 4, md: 6 },
        paddingTop: { xs: 4, md: 6 },
        backgroundColor: 'grey.100',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <StyledContainer maxWidth='lg'>
        {/* Breadcrumbs navigation */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize='small' />}
          aria-label='breadcrumb'
          sx={{ mb: 3 }}
        >
          {breadcrumbItems.map((item, index) =>
            item.isActive ? (
              <Typography key={index} color='text.primary'>
                {item.label}
              </Typography>
            ) : (
              <Link
                key={index}
                color='inherit'
                href={item.href}
                sx={{
                  textDecoration: 'none',

                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {item.label}
              </Link>
            )
          )}
          {/* <Link
            color='inherit'
            href={`/laws/${country}/${lawShortName}`}
            sx={{
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {lawShortName.replaceAll('-', ' ').toUpperCase()}
          </Link> */}
          {/* <Typography color='text.primary'>{formatSectionTitle()}</Typography> */}
        </Breadcrumbs>

        {/* Animated Header Section */}
        <AnimatedHeader>
          <Typography
            variant='h4'
            component='h1'
            sx={{
              fontSize: { xs: '1.875rem', md: '2.25rem' },
              fontWeight: 600,
              mb: 1,
              color: themeColors.primary,
            }}
          >
            {`${sectionInfo.title} Articles`}
          </Typography>
          {/* <Typography
            variant='body1'
            color='text.secondary'
            sx={{ maxWidth: '768px' }}
          >
            Explore all articles that comes under {formatSectionTitle()}.
          </Typography> */}
        </AnimatedHeader>

        {/* Section Header with Icon */}
        <SectionHeader>
          <IconContainer>
            <MenuBookOutlinedIcon fontSize='large' />
          </IconContainer>
          <Box>
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{ maxWidth: '768px' }}
            >
              Explore all articles under {sectionInfo.title}.
            </Typography>
            {/* <Typography
              variant='h5'
              component='h2'
              sx={{
                fontWeight: 600,
                color: themeColors.primary,
              }}
            >
              {formatSectionTitle()}
            </Typography> */}
            {/* {sectionInfo.path && (
              <Typography variant='body2' color='text.secondary'>
                {formatPathString(sectionInfo.path)}
              </Typography>
            )} */}
          </Box>
        </SectionHeader>

        {/* Loading, Error, and Articles Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress sx={{ color: themeColors.primary }} />
          </Box>
        ) : error ? (
          <Alert severity='error' sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : articles.length === 0 ? (
          <Alert severity='info' sx={{ my: 2 }}>
            No articles found for this section.
          </Alert>
        ) : (
          <Box ref={articlesRef}>
            <Grid container spacing={0.5}>
              {articles.map((article, index) => (
                <Grid
                  item
                  xs={12}
                  md={12} // md={6}
                  lg={12} // lg={4}
                  key={article.id || index}
                  className='article-item'
                  sx={{
                    opacity: 0,
                    animation: 'none',
                    '&.animate': {
                      animation: 'fadeInUp 0.5s ease forwards',
                    },
                    '@keyframes fadeInUp': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(20px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                  }}
                >
                  <ArticleCard article={article} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </StyledContainer>
    </Box>
  )
}

export default HierarchicalArticlesView
